import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { handleCreateMessage } from "../API/Contact/contactService";
import {
  complaintSchema,
  ProjectComplaintValues,
} from "../components/forms/Complaints/complaints-zod.schema";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const router = useNavigate();

  const form = useForm<ProjectComplaintValues>({
    resolver: zodResolver(complaintSchema),
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();

    try {
      await handleCreateMessage(formData, router);

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  const termsValue = form.watch("terms");

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Formulaire de contact - AQUAVESC</title>
        <meta
          name="description"
          content="Formulaire de contact pour contacter AQUAVESC"
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_SERVER_URL}/contact`}
        />
      </Helmet>
      <div className="container m-20 mx-auto mt-20 mb-20 md:px-40 sm:px-40">
        <div className="text-2xl font-bold text-center">NOUS CONTACTER</div>
        <div className="mt-5 text-center text-neutral-400">
          Complétez et retournez-nous ce formulaire pour toute demande
          d’information. <br />
          Nous nous engageons à vous contacter dans les plus brefs délais.
        </div>
        <Form {...form}>
          <form
            className="m-auto md:mr-24 md:ml-24"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="mt-5 border border-primary "
                        disabled={loading}
                        placeholder="Email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col md:space-x-4 md:flex-row">
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="mt-5 border border-primary md:w-[440px]"
                        disabled={loading}
                        placeholder="Nom"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="mt-5 border border-primary md:w-[432px]"
                        disabled={loading}
                        placeholder="Prénom"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="mt-5 border border-primary"
                        disabled={loading}
                        placeholder="Société"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <div className="flex flex-col justify-between lg:space-x-4 lg:flex-row">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className="mt-5 border   border-primary md:w-[432px]"
                          disabled={loading}
                          placeholder="Ville *"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className="mt-5 border border-primary lg:w-[440px]"
                          disabled={loading}
                          placeholder="Téléphone"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div>
              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="mt-5 border border-primary"
                        disabled={loading}
                        placeholder="Sujet *"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <Textarea
                      rows={20}
                      className="mt-5 border border-primary"
                      placeholder="Écrire votre description ici."
                      id="message-2"
                      {...field}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="text-[#B19D9D] font-bold mt-5">
              * Champs obligatoires
            </div>

            <div className="flex items-center mt-3 space-x-2">
              <FormField
                control={form.control}
                name="terms"
                render={({ field }) => (
                  <>
                    <input
                      id="terms"
                      type="checkbox"
                      checked={field.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(e.target.checked)
                      }
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      J'accepte la politique de confidentialité de ce site.*
                    </label>
                  </>
                )}
              />
            </div>

            {form.formState.errors.terms && (
              <div className="mt-1 text-sm text-red-600">
                {form.formState.errors.terms.message}
              </div>
            )}
            <div className="mt-5">
              <Button
                className="w-full rounded-none md:w-56"
                disabled={loading}
                isLoading={loading}
                type="submit"
              >
                Envoyer votre message
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
};

export default Contact;
